/* eslint-disable @typescript-eslint/no-explicit-any */

import { SharedAppConstants } from '@tock/shared-app-constants';
import { core } from '@tock/coretypes';
import { noop } from 'es-toolkit';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { Visit, VisitPlayNotificationSoundRequest, VisitPrintChitRequest, VisitTagsUpdateRequest } =
  protos;

export function getBookingNote(ticketGroupId: number, purchaseId: number): ApiAction {
  let url = `/note/ticket/group/${ticketGroupId}`;
  if (purchaseId) {
    url += '?purchaseId=' + purchaseId;
  }
  return {
    TockApi: {
      getProto: url,
      type: SharedAppConstants.BOOKING_NOTE_GET,
    },
  };
}

export function getVisitForWalkIn(walkInId: number, serviceDate: any): ApiAction {
  return {
    TockApi: {
      getProto: `/visit?walkInId=${walkInId}&serviceDate=${serviceDate}`,
      type: SharedAppConstants.VISIT_GET,
    },
  };
}

export function getChecksByPurchase(purchaseId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/pos/check/purchase?purchaseId=${purchaseId}`,
      type: SharedAppConstants.VISIT_POS_CHECK_GET,
    },
  };
}

export function getChecksByPartyId(partyId: number, serviceDate: any): ApiAction {
  return {
    TockApi: {
      getListProto: `/pos/check/visit?partyId=${partyId}&serviceDate=${serviceDate}`,
      type: SharedAppConstants.VISIT_POS_CHECK_GET,
      onError: { 1001: noop },
    },
  };
}

export function updateWalkInVisit(
  walkInId: number,
  serviceDate: any,
  visit: core.Visit
): ApiAction {
  const proto = Visit.fromObject({
    ...visit,
    walkInId,
    serviceDate,
  });

  const context = { walkInId };
  return updateVisitInternal(proto, context);
}

export function updateTicketSubsetVisit(ticketSubsetId: number, visit: core.Visit): ApiAction {
  const proto = Visit.fromObject({
    ...visit,
    ticketSubsetId,
  });
  const context = { ticketSubsetId };
  return updateVisitInternal(proto, context);
}

export function updatePurchaseVisit(purchaseId: number, visit: core.Visit): ApiAction {
  const proto = Visit.fromObject({
    ...visit,
    purchaseId,
  });
  const context = { purchaseId };
  return updateVisitInternal(proto, context);
}

function updateVisitInternal(proto: any, context: any) {
  return {
    TockApi: {
      putProto: '/visit',
      type: SharedAppConstants.VISIT_UPDATE,
      request: proto,
      context,
    },
  };
}

export function updateVisitTags(visitId: number, visitTag: core.IVisitTag[]) {
  return {
    TockApi: {
      putProto: '/visit/tag',
      type: SharedAppConstants.VISIT_UPDATE,
      request: VisitTagsUpdateRequest.fromObject({
        visitId,
        visitTag,
      }),
    },
  };
}

export function printVisitChit(visitId: number) {
  return {
    TockApi: {
      postProto: `/visit/print`,
      type: AppConstants.CHITS_AUTO_PRINT_FOR_ORDER,
      request: VisitPrintChitRequest.fromObject({
        visitId,
        chitPrinted: true,
      }),
    },
  };
}

export function printVisitChitFailed(visitId: number) {
  return {
    TockApi: {
      postProto: `/visit/print`,
      type: AppConstants.CHITS_AUTO_PRINT_FOR_ORDER,
      request: VisitPrintChitRequest.fromObject({
        visitId,
        chitPrinted: false,
      }),
    },
  };
}

/**
 * @param visitId the visit the sound was played for.
 * @param forCreation true to indicate that the sound was played to indicate the order was just created, vs. due soon.
 */
export function playOrderNotificationSound(visitId: number, forCreation: boolean) {
  return {
    TockApi: {
      postProto: `/visit/play-notification-sound`,
      type: AppConstants.ORDER_NOTIFICATIONS_PLAY_SOUND,
      request: VisitPlayNotificationSoundRequest.fromObject({
        visitId,
        notificationSoundPlayed: true,
        forCreation: forCreation,
      }),
    },
  };
}

/**
 * @param visitId the visit the sound failed to play for.
 * @param forCreation true to indicate that the sound was played to indicate the order was just created, vs. due soon.
 */
export function playOrderNotificationSoundFailed(visitId: number, forCreation: boolean) {
  return {
    TockApi: {
      postProto: `/visit/play-notification-sound`,
      type: AppConstants.ORDER_NOTIFICATIONS_SOUND_PLAY_FAILED,
      request: VisitPlayNotificationSoundRequest.fromObject({
        visitId,
        notificationSoundPlayed: false,
        forCreation: forCreation,
      }),
    },
  };
}

export function getBookingNoteByBusiness(
  ticketGroupId: number,
  purchaseId: number,
  scope: any
): ApiAction {
  return {
    TockApi: {
      getProto: '/note/ticket/group/' + ticketGroupId + '?purchaseId=' + purchaseId,
      type: SharedAppConstants.BOOKING_NOTE_GET,
      scope,
    },
  };
}

export function getTicketGroup(ticketGroupId: number): ApiAction | BaseTockAction {
  if (!ticketGroupId) {
    window.LOGGER?.error(
      'BookingActions.getTicketGroup called with undefined as the ticket groupId'
    );
    return {
      type: SharedAppConstants.NULL,
    };
  }
  return {
    TockApi: {
      getProto: '/ticket/group/' + ticketGroupId,
      type: SharedAppConstants.TICKET_GROUP_GET,
    },
  };
}

export function getCurrentPurchaseById(purchaseId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/purchase/${purchaseId}/latest`,
      type: SharedAppConstants.PURCHASE_GET,
    },
  };
}

export function sendConfirmation(
  purchaseId: number,
  emailOverride: any,
  skipReceipt: any
): ApiAction {
  const escapedEmails = encodeURIComponent(emailOverride);
  return {
    TockApi: {
      getProto: `/admin/purchase/resend/${purchaseId}?emailOverride=${escapedEmails}&skipReceipt=${
        skipReceipt ? 'true' : 'false'
      }`,
      type: SharedAppConstants.NULL,
      toast: 'Confirmation email was sent',
    },
  };
}

export function sendSurvey(purchaseId: number): ApiAction {
  return {
    TockApi: {
      getProto: `/admin/purchase/resend/${purchaseId}?emailType=survey`,
      type: SharedAppConstants.QUESTION_RESENT,
      toast: 'Pre-visit questions email was sent',
    },
  };
}

export function getPurchaseTimelineEvents(purchaseId: number): ApiAction {
  return {
    TockApi: {
      getListProto: `/purchase/timeline/events?purchaseId=${purchaseId}`,
      type: SharedAppConstants.PURCHASE_FETCH_TIMELINE_EVENTS,
    },
  };
}
